import { PlatformPageObject } from "@tracabois/template";
import { Button, Stack } from "@mui/material";
import {
  isEditableRealization,
  RealizationStateChip,
  useGetRealizationByIdQuery,
  useRealizationDeleteMutation,
  useRealizationUpdateMutation,
  OuvrageCreatedEvent,
  RealizationDescriptionSection,
  RealizationCertificationLinkSection,
  RealizationGallerySection,
  RealizationInfoSection,
  RealizationBdaSection,
  RealizationEnvironmentalSection,
  RealizationActorsSection,
  RealizationOriginesSection,
  RealizationOuvrageSection,
  RealizationUpdateCommand,
  realizationToUpdateRealizationCommand,
} from "@tracabois/domain";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ouvrageRoute } from "../../ouvrage/route";
import { ObjectHeader, QrcodeImg } from "@tracabois/components";
import { DeleteButton } from "@tracabois/components";
import { goto } from "../route/realization.goto";

const useLabels = () => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      description: t("realization.description", "Description du projet"),
      ouvrage: t("ouvrage.plurals", "Ouvrages"),
      certification: t("realization.certification", "Reconnaissances"),
      save: t("common.save", "Enregistrer"),
    }),
    [t]
  );
};

type RealizationIdUrl = {
  realizationId: string;
};

export const RealizationViewPage = () => {
  const navigate = useNavigate();
  const { realizationId = "" } = useParams<RealizationIdUrl>();
  const labels = useLabels();
  const realizationResult = useGetRealizationByIdQuery({ id: realizationId });
  const [realizationDelete] = useRealizationDeleteMutation();
  const [realizationUpdate, { isLoading: isSaving }] = useRealizationUpdateMutation();
  const isEditable = isEditableRealization(realizationResult.data);

  // Global form state
  const [formValues, setFormValues] = useState<Partial<RealizationUpdateCommand>>({});
  const [description, setDescription] = useState<string>("");

  // Initialize form values when realization data is loaded
  useEffect(() => {
    if (realizationResult.data) {
      setFormValues(realizationToUpdateRealizationCommand(realizationResult.data));
      setDescription(realizationResult.data.description || "");
    }
  }, [realizationResult.data]);

  const onRealizationStateChanged = useCallback(() => {
    realizationResult.refetch();
  }, [realizationResult]);

  const gotoOuvrage = useCallback(
    (ouvrage: OuvrageCreatedEvent) => {
      ouvrageRoute.goto.viewOuvrage(ouvrage.id, navigate);
    },
    [navigate]
  );

  const deleteAction = useCallback(async (): Promise<boolean> => {
    await realizationDelete({
      id: realizationId,
    });
    goto.listRealization(navigate);
    return true;
  }, [realizationId, realizationDelete, navigate]);

  const assignMoe = useCallback(async () => {
    realizationResult.refetch();
    return true;
  }, [realizationResult]);

  // Global save handler
  const handleSave = useCallback(async () => {
    if (!realizationResult.data) return;

    await realizationUpdate({
      ...formValues,
      id: realizationId,
      name: formValues.name || realizationResult.data.name,
      description: description,
    });

    realizationResult.refetch();
  }, [realizationUpdate, realizationId, formValues, description, realizationResult]);

  const isLoading = realizationResult.isLoading || realizationResult.isFetching;
  const readOnly = !isEditable;

  return (
    <PlatformPageObject>
      <ObjectHeader
        title={realizationResult.data?.name || ""}
        actions={
          <>
            {isEditable && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {labels.save}
              </Button>
            )}
            {isEditable && <DeleteButton onClick={deleteAction} />}
            <RealizationStateChip
              editable
              onStateChanged={onRealizationStateChanged}
              item={realizationResult.data}
            />
            <QrcodeImg
              certificateId={realizationId}
              qrcode={realizationResult.data?.qrcode}
            />
          </>
        }
      />

      <Stack direction="column" gap={3}>
        {/* Header with gallery */}
        <RealizationGallerySection
          realization={realizationResult.data}
          editable={isEditable}
          onRealizationUpdated={onRealizationStateChanged}
        />

        {/* Informations generales */}
        <RealizationInfoSection
          realization={realizationResult.data}
          formValues={formValues}
          onChange={setFormValues}
          readOnly={readOnly}
        />

        {/* Description du projet */}
        <RealizationDescriptionSection
          description={description}
          onChange={setDescription}
          readOnly={readOnly}
          title={labels.description}
        />

        {/* Left (Autres + Indicateurs + Reconnaissances) | Right (Acteurs + Origines + Ouvrages) */}
        <Stack direction={{ xs: "column", md: "row" }} gap={3}>
          <Stack flex={1} gap={3} sx={{ minWidth: 0 }}>
            <RealizationBdaSection
              realization={realizationResult.data}
              formValues={formValues}
              onChange={setFormValues}
              readOnly={readOnly}
            />
            <RealizationEnvironmentalSection
              realization={realizationResult.data}
              formValues={formValues}
              onChange={setFormValues}
              readOnly={readOnly}
            />
            <RealizationCertificationLinkSection
              realization={realizationResult.data}
              title={labels.certification}
              editable={isEditable}
              onRealizationUpdated={onRealizationStateChanged}
            />
          </Stack>
          <Stack flex={2} gap={3} sx={{ minWidth: 0 }}>
            <RealizationActorsSection
              realization={realizationResult.data}
              onMoeAssigned={assignMoe}
              onMoeRemoved={assignMoe}
              onActorAssigned={onRealizationStateChanged}
              isLoading={isLoading}
              editable={isEditable}
            />
            <RealizationOriginesSection
              realization={realizationResult.data}
              onOriginAdded={onRealizationStateChanged}
              onOriginRemoved={onRealizationStateChanged}
              isLoading={isLoading}
              editable={isEditable}
            />
            <RealizationOuvrageSection
              onOuvrageClicked={(item) =>
                ouvrageRoute.goto.viewOuvrage(item.id, navigate)
              }
              onOuvrageAdded={gotoOuvrage}
              realization={realizationResult.data}
              isLoading={isLoading}
              editable={isEditable}
              title={labels.ouvrage}
            />
          </Stack>
        </Stack>

      </Stack>
    </PlatformPageObject>
  );
};
